import { axiosGetV3 } from '../../api/axios-handler';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Content, Resource, People } from './useExperiences';
import { fetchVimeoMetadataBatch, addVimeoMetadataToExperiences } from './useBrowseExperiences';
import { useCrisisCheck, isCrisisCheckInCooldown } from './useCrisisCheck';

export type AlgoliaSearchResult =
  | (Content & { source: 'content' })
  | (Resource & { source: 'resources' })
  | (People & { source: 'user' });

export interface AlgoliaSearchResponse {
  count: number;
  counts_by_source?: {
    // New format
    video?: number;
    audio?: number;
    resources?: number;
    peers?: number;
    // Old format (for backward compatibility)
    content?: number;
    user?: number;
  };
  data: AlgoliaSearchResult[];
}

const getAlgoliaSearch = async (query: string, tagGroupId?: number): Promise<AlgoliaSearchResponse> => {
  if (!query || query.trim().length === 0) {
    return {
      count: 0,
      data: [],
    };
  }

  const params: Record<string, string> = {
    q: query.trim(),
  };

  if (tagGroupId) {
    params.tag_groups = tagGroupId.toString();
  }

  const response = await axiosGetV3('/simple_search/algolia/search', params);
  const searchData = response.data;

  // Add vimeo metadata to video content items
  if (searchData.data && searchData.data.length > 0) {
    // Filter video items that need vimeo metadata
    const videoItems = searchData.data.filter((item: AlgoliaSearchResult) => {
      if (item.source !== 'content') return false;
      const content = item as Content & { source: 'content' };
      return (
        'media_type' in content && content.media_type === 'VIDEO' && content.video_id && !content.vimeo_thumbnail_url
      );
    }) as (Content & { source: 'content' })[];

    if (videoItems.length > 0) {
      // Fetch vimeo metadata for all video items
      const videoIds = videoItems.map((item) => item.video_id).filter(Boolean) as string[];
      const metadataMap = await fetchVimeoMetadataBatch(videoIds);

      // Add vimeo metadata to the search results
      searchData.data = searchData.data.map((item: AlgoliaSearchResult) => {
        if (item.source === 'content') {
          const content = item as Content & { source: 'content' };
          const mediaType = 'media_type' in content ? content.media_type : undefined;
          if (mediaType === 'VIDEO' && content.video_id) {
            return addVimeoMetadataToExperiences(content, metadataMap);
          }
        }
        return item;
      });
    }
  }

  return searchData;
};

export const useAlgoliaSearch = (query: string, tagGroupId?: number) => {
  const trimmedQuery = query?.trim() || '';
  const isEnabled = trimmedQuery.length > 0;
  const crisisCheck = useCrisisCheck();
  const lastCheckedQuery = useRef<string>('');

  const searchQuery = useQuery<AlgoliaSearchResponse>(
    ['algoliaSearch', trimmedQuery, tagGroupId],
    () => getAlgoliaSearch(trimmedQuery, tagGroupId),
    {
      enabled: isEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  useEffect(() => {
    if (trimmedQuery && trimmedQuery !== lastCheckedQuery.current && !isCrisisCheckInCooldown()) {
      crisisCheck.mutate(trimmedQuery);
      lastCheckedQuery.current = trimmedQuery;
    }
  }, [trimmedQuery]);

  return searchQuery;
};
